import { useEffect, useRef, useState } from 'react';
import type { TimeHTMLAttributes } from 'react';
import { MS_PER_SECOND } from '@/utils';
import { Typography } from '../Typography';
import { formatCountdown } from './formatCountdown';
import type { CountdownFormat } from './formatCountdown';

export type CountdownSize = 'medium' | 'large' | 'huge';
export type CountdownEmphasis = 'regular' | 'bold';

export interface CountdownProps extends Omit<
  TimeHTMLAttributes<HTMLTimeElement>,
  'children' | 'className' | 'color' | 'dateTime' | 'style'
> {
  /** Absolute expiration time as milliseconds since the Unix epoch. */
  expiresAt: number;
  format?: CountdownFormat;
  size?: CountdownSize;
  emphasis?: CountdownEmphasis;
  onExpire?: () => void;
}

function getRemainingSeconds(expiresAt: number): number {
  if (!Number.isFinite(expiresAt)) return 0;
  return Math.max(0, Math.ceil((expiresAt - Date.now()) / MS_PER_SECOND));
}

/** Displays time remaining until an absolute expiration timestamp. */
export function Countdown({
  expiresAt,
  format = 'seconds',
  size = 'medium',
  emphasis = 'regular',
  onExpire,
  'aria-label': ariaLabel = 'Time remaining',
  ...timeProps
}: CountdownProps) {
  const [remainingSeconds, setRemainingSeconds] = useState(() => getRemainingSeconds(expiresAt));
  const onExpireRef = useRef(onExpire);
  const emittedExpirationRef = useRef<number | null>(null);

  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  useEffect(() => {
    let intervalId: number | undefined;

    const updateRemainingTime = () => {
      const nextRemainingSeconds = getRemainingSeconds(expiresAt);
      setRemainingSeconds(nextRemainingSeconds);

      if (nextRemainingSeconds === 0 && intervalId !== undefined) {
        window.clearInterval(intervalId);
        intervalId = undefined;
        document.removeEventListener('visibilitychange', updateRemainingTime);
      }

      if (
        Number.isFinite(expiresAt) &&
        nextRemainingSeconds === 0 &&
        emittedExpirationRef.current !== expiresAt
      ) {
        emittedExpirationRef.current = expiresAt;
        onExpireRef.current?.();
      }

      return nextRemainingSeconds;
    };

    const initialRemainingSeconds = updateRemainingTime();

    if (!Number.isFinite(expiresAt) || initialRemainingSeconds === 0) return;

    intervalId = window.setInterval(updateRemainingTime, MS_PER_SECOND);
    document.addEventListener('visibilitychange', updateRemainingTime);

    return () => {
      if (intervalId !== undefined) window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', updateRemainingTime);
    };
  }, [expiresAt]);

  const formattedRemainingTime = formatCountdown(remainingSeconds, format);

  return (
    <Typography
      {...(size === 'huge'
        ? { variant: 'display' as const, size: 'huge' as const }
        : { variant: 'body' as const, size })}
      as="time"
      dateTime={`PT${remainingSeconds}S`}
      aria-label={`${ariaLabel}: ${formattedRemainingTime}`}
      decoration={emphasis === 'bold' ? { bold: true } : undefined}
      {...timeProps}
    >
      {formattedRemainingTime}
    </Typography>
  );
}
